<template>
  <div class="product-config-step">
    <div class="config-layout">
      <activity-preview-panel
        v-model="activeModuleKey"
        :modules="previewModules"
        @action="onPreviewAction"
        @preview="onPreviewEffect"
      />

      <activity-banner-module
        v-if="activeModuleType === 'banner'"
        :key="'banner-' + activeModuleKey"
        :value="activeModule"
      />

      <activity-coupon-module
        v-else-if="activeModuleType === 'coupon'"
        :key="'coupon-' + activeModuleKey"
        :value="activeModule"
      />

      <activity-focus-module
        v-else-if="activeModuleType === 'focus'"
        :key="'focus-' + activeModuleKey"
        :value="activeModule"
      />

      <activity-list-module
        v-else-if="activeModuleType === 'list'"
        :key="'list-' + activeModuleKey"
        :value="activeModule"
      />

      <activity-module-placeholder
        v-else
        :title="activeModuleLabel"
      />
    </div>

    <div class="footer-actions">
      <a-button @click="$emit('prev')">上一步</a-button>
      <a-button type="primary" @click="$emit('next')">下一步</a-button>
    </div>

    <a-modal
      title="效果预览"
      :visible="previewVisible"
      :footer="null"
      :width="420"
      destroy-on-close
      @cancel="previewVisible = false"
    >
      <div class="h5-preview-frame">
        <iframe
          v-if="previewVisible && previewUrl"
          class="h5-preview-iframe"
          :src="previewUrl"
          frameborder="0"
        />
        <div v-else class="h5-preview-empty">暂无预览地址</div>
      </div>
    </a-modal>

    <a-modal
      title="选择模块类型"
      :visible="addModuleVisible"
      :footer="null"
      :width="360"
      destroy-on-close
      @cancel="closeAddModule"
    >
      <div class="module-type-list">
        <a-button
          v-for="item in moduleTypeOptions"
          :key="item.type"
          block
          class="module-type-btn"
          @click="confirmAddModule(item.type)"
        >
          {{ item.label }}
        </a-button>
      </div>
    </a-modal>
  </div>
</template>

<script>
import RouterSwitchHelper from '@/utils/helper/RouterSwitchHelper'
import ActivityPreviewPanel from './productConfig/ActivityPreviewPanel.vue'
import ActivityBannerModule from './productConfig/ActivityBannerModule.vue'
import ActivityCouponModule from './productConfig/ActivityCouponModule.vue'
import ActivityFocusModule from './productConfig/ActivityFocusModule.vue'
import ActivityListModule from './productConfig/ActivityListModule.vue'
import ActivityModulePlaceholder from './productConfig/ActivityModulePlaceholder.vue'

const H5_ACTIVITY_PATH = '/marketing/activity'

const MODULE_DEFS = {
  banner: { label: '活动顶图', type: 'normal', componentType: 1 },
  coupon: { label: '领券模块', type: 'normal', componentType: 2 },
  focus: { label: '商品切换焦点', type: 'normal', componentType: 3 },
  list: { label: '商品列表', type: 'normal', componentType: 4 }
}

/** 左侧预览：按模块样式类型展示缩略图（与右侧样式选项图片保持一致） */
const MODULE_STYLE_COVERS = {
  coupon: {
    1: 'https://imgbtest.woyoubooking.com/test/base/42dcffaf8a784a759d7fb315a23621a2.png',
    2: 'https://imgbtest.woyoubooking.com/test/base/1ed0c7c0b44c4c2e8788df185eb18d3e.png',
    3: 'https://imgbtest.woyoubooking.com/test/base/19db7520f4a64354b105874172cfc544.png',
    4: 'https://imgbtest.woyoubooking.com/test/base/42dcffaf8a784a759d7fb315a23621a2.png'
  },
  focus: {
    1: 'https://imgbtest.woyoubooking.com/test/base/71513b8728164ed880be14f9bdf22df8.png',
    2: 'https://imgbtest.woyoubooking.com/test/base/533ab46c6a194824bb31619cd2e6048f.png',
    3: 'https://imgbtest.woyoubooking.com/test/base/aa551a27341047999a98982b46a94ba4.png',
    4: 'https://imgbtest.woyoubooking.com/test/base/297d222a3b334f8190376fdab29a683e.png'
  },
  list: {
    1: '',
    2: 'https://imgbtest.woyoubooking.com/test/base/5b3a661ebaf84870a8e85381b9f2b9ef.png',
    3: '',
    4: ''
  }
}

function getModuleCover (item) {
  if (!item) return ''
  if (item.moduleType === 'banner') return item.topImage || ''
  const styleMap = MODULE_STYLE_COVERS[item.moduleType]
  if (!styleMap) return ''
  const styleType = item.switchStyleType != null ? item.switchStyleType : 1
  return styleMap[styleType] || ''
}

let moduleKeySeed = 1
function createModuleKey () {
  return `m_${Date.now()}_${moduleKeySeed++}`
}

const COMPONENT_TYPE_MAP = {
  1: 'banner',
  2: 'coupon',
  3: 'focus',
  4: 'list'
}

function createDoneFile (url, uidPrefix = 'f') {
  if (!url) return null
  return {
    uid: `${uidPrefix}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    name: 'image',
    status: 'done',
    url
  }
}

function createModule (moduleType) {
  const base = {
    key: createModuleKey(),
    moduleType
  }
  if (moduleType === 'banner') {
    return {
      ...base,
      switchStyleType: 0,
      topImage: '',
      // 活动背景色暂存在顶图模块表单，提交时提到活动根字段 backgroundColor
      backgroundColor: ''
    }
  }
  if (moduleType === 'coupon') {
    return {
      ...base,
      switchStyleType: 1,
      coupons: []
    }
  }
  if (moduleType === 'focus') {
    return {
      ...base,
      switchStyleType: 1,
      focusTitleType: 1,
      focusTitle: '',
      focusSubTitleType: 1,
      focusSubTitle: '',
      tabs: [],
      products: []
    }
  }
  if (moduleType === 'list') {
    return {
      ...base,
      switchStyleType: 1,
      focusTitleType: 1,
      focusTitle: '',
      focusSubTitleType: 1,
      focusSubTitle: '',
      // 酒店商品列表默认值（提交时 JSON 写入 topImage）
      defaultCity: '',
      allowLocateCity: 0,
      defaultStarIds: [],
      defaultBrandIds: [],
      defaultThemeIds: [],
      defaultTypeIds: []
    }
  }
  return base
}

/** 商品列表未使用 topImage，复用该字段存酒店默认值 JSON */
function buildListHotelDefaultsPayload (item = {}) {
  return {
    defaultCity: item.defaultCity || '',
    allowLocateCity: item.allowLocateCity != null ? Number(item.allowLocateCity) : 0,
    defaultStarIds: Array.isArray(item.defaultStarIds) ? item.defaultStarIds : [],
    defaultBrandIds: Array.isArray(item.defaultBrandIds) ? item.defaultBrandIds : [],
    defaultThemeIds: Array.isArray(item.defaultThemeIds) ? item.defaultThemeIds : [],
    defaultTypeIds: Array.isArray(item.defaultTypeIds) ? item.defaultTypeIds : []
  }
}

function encodeListHotelDefaults (item) {
  try {
    return JSON.stringify(buildListHotelDefaultsPayload(item))
  } catch (e) {
    return ''
  }
}

function decodeListHotelDefaults (topImage) {
  const empty = buildListHotelDefaultsPayload()
  if (!topImage || typeof topImage !== 'string') {
    return empty
  }
  const text = topImage.trim()
  if (!text || text.charAt(0) !== '{') {
    return empty
  }
  try {
    const data = JSON.parse(text)
    if (!data || typeof data !== 'object') {
      return empty
    }
    return buildListHotelDefaultsPayload(data)
  } catch (e) {
    return empty
  }
}

function mapProductRow (product = {}, tabName = '') {
  return {
    proId: product.proId,
    productName: product.proName || '',
    statusName: product.statusStr || '',
    status: product.status,
    tabName: tabName || product.tabName || ''
  }
}

function mapFocusFromDetail (comp) {
  const mod = createModule('focus')
  mod.componentId = comp.componentId
  mod.switchStyleType = comp.switchStyleType != null ? comp.switchStyleType : 1
  mod.focusTitleType = comp.focusTitleType || 1
  mod.focusTitle = comp.focusTitle || ''
  mod.focusSubTitleType = comp.focusSubTitleType || 1
  mod.focusSubTitle = comp.focusSubTitle || ''

  const apiTabs = Array.isArray(comp.tabs) ? comp.tabs : []
  if (apiTabs.length) {
    mod.tabs = apiTabs.map((tab, index) => {
      const tabImage = tab.tabImage || ''
      const unChoiceTabImage = tab.unChoiceTabImage || ''
      const selectedFile = createDoneFile(tabImage, `tab_sel_${index}`)
      const unselectedFile = createDoneFile(unChoiceTabImage, `tab_unsel_${index}`)
      return {
        id: `tab_${tab.tabId != null ? tab.tabId : index}_${Date.now()}`,
        tabId: tab.tabId,
        tabName: tab.tabName || '',
        sortNo: tab.sortNo != null ? tab.sortNo : index + 1,
        tabImage,
        selectedImage: tabImage,
        unChoiceTabImage,
        selectedFileList: selectedFile ? [selectedFile] : [],
        unselectedFileList: unselectedFile ? [unselectedFile] : [],
        products: []
      }
    })
    // tabs 内商品 + 顶层未打标签商品，合并回填，避免丢数据
    const fromTabs = apiTabs.reduce((list, tab) => {
      const tabName = tab.tabName || ''
      const rows = (tab.products || []).map(product => mapProductRow(product, tabName))
      return list.concat(rows)
    }, [])
    const existIds = new Set(fromTabs.map(item => String(item.proId)))
    const fromRoot = (comp.products || [])
      .map(product => mapProductRow(product))
      .filter(item => !existIds.has(String(item.proId)))
    mod.products = fromTabs.concat(fromRoot)
  } else {
    mod.tabs = []
    mod.products = (comp.products || []).map(product => mapProductRow(product))
  }
  return mod
}

function mapComponentToModule (comp) {
  const moduleType = COMPONENT_TYPE_MAP[comp.componentType]
  if (!moduleType) return null
  if (moduleType === 'focus') return mapFocusFromDetail(comp)

  const mod = createModule(moduleType)
  mod.componentId = comp.componentId
  if (moduleType === 'banner') {
    mod.topImage = comp.topImage || ''
    mod.switchStyleType = 0
    mod.backgroundColor = ''
  } else if (moduleType === 'coupon') {
    mod.switchStyleType = comp.switchStyleType != null ? comp.switchStyleType : 1
    mod.coupons = (comp.coupons || []).map(coupon => ({
      couponCode: coupon.couponCode,
      couponName: coupon.couponName || coupon.couponCode || '',
      statusName: coupon.statusStr || ''
    }))
  } else if (moduleType === 'list') {
    mod.switchStyleType = comp.switchStyleType != null ? comp.switchStyleType : 1
    mod.focusTitleType = comp.focusTitleType || 1
    mod.focusTitle = comp.focusTitle || ''
    mod.focusSubTitleType = comp.focusSubTitleType || 1
    mod.focusSubTitle = comp.focusSubTitle || ''
    // topImage 存的是酒店默认值 JSON，解析回表单字段
    Object.assign(mod, decodeListHotelDefaults(comp.topImage))
  }
  return mod
}

export default {
  name: 'ActivityProductConfig',
  components: {
    ActivityPreviewPanel,
    ActivityBannerModule,
    ActivityCouponModule,
    ActivityFocusModule,
    ActivityListModule,
    ActivityModulePlaceholder
  },
  props: {
    // 编辑详情 components；null 表示新建，不覆盖默认模块
    detailComponents: {
      type: Array,
      default: null
    },
    activityCode: {
      type: String,
      default: ''
    },
    // 活动根字段 backgroundColor，回填到顶图表单
    backgroundColor: {
      type: String,
      default: ''
    }
  },
  data () {
    const modules = [
      createModule('banner'),
      createModule('coupon'),
      createModule('focus'),
      createModule('focus'),
      createModule('list')
    ]
    return {
      modules,
      activeModuleKey: modules[0].key,
      previewVisible: false,
      previewUrl: '',
      addModuleVisible: false,
      addModuleAnchorKey: ''
    }
  },
  watch: {
    detailComponents: {
      immediate: true,
      handler (list) {
        if (!Array.isArray(list)) return
        this.setModulesFromDetail(list)
        this.applyBackgroundColor(this.backgroundColor)
      }
    },
    backgroundColor: {
      immediate: true,
      handler (val) {
        this.applyBackgroundColor(val)
      }
    }
  },
  computed: {
    activeModule () {
      return this.modules.find(item => item.key === this.activeModuleKey) || null
    },
    activeModuleType () {
      return this.activeModule ? this.activeModule.moduleType : ''
    },
    activeModuleLabel () {
      const def = MODULE_DEFS[this.activeModuleType]
      return def ? def.label : ''
    },
    previewModules () {
      return this.modules.map((item, index) => {
        const def = MODULE_DEFS[item.moduleType] || { label: '未知模块', type: 'normal' }
        const actions = []
        if (index > 0) actions.push('上移')
        if (index < this.modules.length - 1) actions.push('下移')
        actions.push('新增', '删除')
        return {
          key: item.key,
          label: def.label,
          type: def.type,
          cover: getModuleCover(item),
          actions
        }
      })
    },
    moduleTypeOptions () {
      return Object.keys(MODULE_DEFS).map(type => ({
        type,
        label: MODULE_DEFS[type].label
      }))
    }
  },
  methods: {
    buildPreviewUrl () {
      const activityCode = this.activityCode || this.$route.query.activityCode || ''
      const platformConfig = this.$store.getters.platformConfig || {}
      const appConfig = this.$store.getters.appConfig || {}
      const domainUrl = (platformConfig.newh5Url || appConfig.newh5Url || platformConfig.h5Url || '').replace(/\/+$/, '')
      if (!domainUrl || !activityCode) return ''
      const merchantId = this.$store.getters.userInfo && this.$store.getters.userInfo.merchantId
      if (!merchantId) return ''
      // p = character_operatorId_merchantId（与销售任务 / H5 移动端地址一致）
      const character = 1
      return RouterSwitchHelper.getTargetUrl(domainUrl + H5_ACTIVITY_PATH, {
        activityCode,
        p: `${character}_${merchantId}_${merchantId}`
      })
    },
    onPreviewEffect () {
      const url = this.buildPreviewUrl()
      if (!url) {
        this.$message.warning('缺少活动编码，请先保存活动后再预览')
        return
      }
      // 编辑时通知父组件同步保存，保证预览内容为最新
      if (this.activityCode) {
        this.$emit('preview-save')
      }
      this.previewUrl = url
      this.previewVisible = true
    },
    onPreviewAction ({ action, key }) {
      if (action === '上移') this.moveModule(key, -1)
      else if (action === '下移') this.moveModule(key, 1)
      else if (action === '新增') this.openAddModule(key)
      else if (action === '删除') this.removeModule(key)
    },
    moveModule (key, offset) {
      const index = this.modules.findIndex(item => item.key === key)
      const target = index + offset
      if (index < 0 || target < 0 || target >= this.modules.length) return
      const list = this.modules.slice()
      const [current] = list.splice(index, 1)
      list.splice(target, 0, current)
      this.modules = list
    },
    openAddModule (key) {
      this.addModuleAnchorKey = key
      this.addModuleVisible = true
    },
    closeAddModule () {
      this.addModuleVisible = false
      this.addModuleAnchorKey = ''
    },
    confirmAddModule (moduleType) {
      const index = this.modules.findIndex(item => item.key === this.addModuleAnchorKey)
      if (index < 0) {
        this.closeAddModule()
        return
      }
      const next = createModule(moduleType)
      const list = this.modules.slice()
      list.splice(index + 1, 0, next)
      this.modules = list
      this.activeModuleKey = next.key
      this.closeAddModule()
    },
    removeModule (key) {
      if (this.modules.length <= 1) {
        this.$message.warning('至少保留一个模块')
        return
      }
      const index = this.modules.findIndex(item => item.key === key)
      if (index < 0) return
      const list = this.modules.slice()
      list.splice(index, 1)
      this.modules = list
      if (this.activeModuleKey === key) {
        const next = list[index] || list[index - 1]
        this.activeModuleKey = next.key
      }
    },
    buildFocusProducts (list = []) {
      return list.map((product, productIndex) => ({
        proId: Number(product.proId),
        sortNo: productIndex + 1
      }))
    },
    // 有 Tab 时：商品按 tabName 归到 tabs[].products，只保留接口字段
    buildFocusTabs (item) {
      const tabs = item.tabs || []
      const products = item.products || []
      const namedTabs = tabs.filter(tab => (tab.tabName || '').trim())
      if (!namedTabs.length) return []
      return namedTabs.map((tab, tabIndex) => {
        const tabName = (tab.tabName || '').trim()
        const tabProducts = products.filter(product => (product.tabName || '').trim() === tabName)
        return {
          sortNo: tabIndex + 1,
          tabName,
          tabImage: tab.tabImage || tab.selectedImage || '',
          unChoiceTabImage: tab.unChoiceTabImage || '',
          products: this.buildFocusProducts(tabProducts)
        }
      })
    },
    buildFocusPayload (item, base) {
      const tabs = this.buildFocusTabs(item)
      const allProducts = item.products || []
      const payload = {
        ...base,
        focusTitleType: item.focusTitleType || 1,
        focusTitle: item.focusTitle || '',
        focusSubTitleType: item.focusSubTitleType || 1,
        focusSubTitle: item.focusSubTitle || ''
      }
      if (tabs.length) {
        // 有 tabs：已打标签商品进 tabs；未打标签商品保留在顶层 products，避免再次编辑丢失
        payload.tabs = tabs
        const namedTabNames = new Set(tabs.map(tab => tab.tabName))
        const unassigned = allProducts.filter(product => {
          const name = (product.tabName || '').trim()
          return !name || !namedTabNames.has(name)
        })
        if (unassigned.length) {
          payload.products = this.buildFocusProducts(unassigned)
        }
      } else {
        payload.products = this.buildFocusProducts(allProducts)
      }
      return payload
    },
    // 详情回填：按 sortNo 还原 modules
    setModulesFromDetail (components = []) {
      const sorted = (components || []).slice().sort((a, b) => (a.sortNo || 0) - (b.sortNo || 0))
      const modules = sorted.map(mapComponentToModule).filter(Boolean)
      if (!modules.length) {
        this.modules = [
          createModule('banner'),
          createModule('coupon'),
          createModule('focus'),
          createModule('list')
        ]
      } else {
        this.modules = modules
      }
      this.activeModuleKey = this.modules[0].key
    },
    applyBackgroundColor (val) {
      const banner = this.modules.find(item => item.moduleType === 'banner')
      if (!banner) return
      this.$set(banner, 'backgroundColor', val || '')
    },
    /** 活动根字段 backgroundColor（从顶图表单读取） */
    getBackgroundColor () {
      const banner = this.modules.find(item => item.moduleType === 'banner')
      return (banner && banner.backgroundColor) || ''
    },
    // 提交时再映射成接口字段
    getComponentsPayload () {
      return this.modules.map((item, index) => {
        const def = MODULE_DEFS[item.moduleType]
        const base = {
          componentType: def.componentType,
          sortNo: index + 1,
          switchStyleType: item.switchStyleType != null
            ? item.switchStyleType
            : (def.componentType === 1 ? 0 : 1)
        }
        if (item.componentId != null) {
          base.componentId = item.componentId
        }
        if (item.moduleType === 'banner') {
          return {
            ...base,
            switchStyleType: 0,
            topImage: item.topImage || ''
          }
        }
        if (item.moduleType === 'coupon') {
          return {
            ...base,
            coupons: (item.coupons || []).map((coupon, couponIndex) => ({
              couponCode: coupon.couponCode,
              sortNo: couponIndex + 1
            }))
          }
        }
        if (item.moduleType === 'focus') {
          return this.buildFocusPayload(item, base)
        }
        if (item.moduleType === 'list') {
          return {
            ...base,
            focusTitleType: item.focusTitleType || 1,
            focusTitle: item.focusTitle || '',
            focusSubTitleType: item.focusSubTitleType || 1,
            focusSubTitle: item.focusSubTitle || '',
            // 酒店列表无顶图，复用 topImage 传默认值 JSON
            topImage: encodeListHotelDefaults(item)
          }
        }
        return base
      })
    }
  }
}
</script>

<style scoped lang="less">
.product-config-step {
  .config-layout {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    min-height: 560px;
  }

  .footer-actions {
    display: flex;
    gap: 12px;
    margin-top: 20px;
  }
}

.h5-preview-frame {
  width: 375px;
  height: 667px;
  margin: 0 auto;
  overflow: hidden;
  background: #1a120b;
  border-radius: 8px;
}

.h5-preview-iframe {
  width: 100%;
  height: 100%;
  border: 0;
}

.h5-preview-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  font-size: 13px;
}

.module-type-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.module-type-btn {
  height: 40px;
}
</style>
